'use client'

import { useEffect, useMemo, useState } from 'react'
import type { AnalyticsReturns } from '@/lib/returns-analytics'
import type { ProductAnalyticsPublicDto } from '@/lib/product-analytics-storage'
import {
  buildReinvestmentForecast,
  ForecastHorizon,
  ForecastProfile,
  type ReinvestmentForecastPoint,
} from '@/lib/reinvestment-forecast'
import {
  buildWeekdayShape,
  weekdayPresetRange,
  WeekdayEvidenceQuality,
  WeekdayKey,
  WeekdaySalesMetric,
  WeekdayShapePreset,
  type WeekdayDateRange,
  type WeekdayShapePoint,
} from '@/lib/weekday-sales-shape'

const PREFERENCES_KEY = 'almaz:analytics-forecast-preferences:v1'
const SHARE_OPTIONS = [0.5, 0.75, 1] as const
const WEEKDAY_LABELS: Record<WeekdayKey, string> = {
  [WeekdayKey.Monday]: 'Пн', [WeekdayKey.Tuesday]: 'Вт', [WeekdayKey.Wednesday]: 'Ср',
  [WeekdayKey.Thursday]: 'Чт', [WeekdayKey.Friday]: 'Пт', [WeekdayKey.Saturday]: 'Сб', [WeekdayKey.Sunday]: 'Вс',
}
const WEEKDAY_NAMES: Record<WeekdayKey, string> = {
  [WeekdayKey.Monday]: 'Понедельник', [WeekdayKey.Tuesday]: 'Вторник', [WeekdayKey.Wednesday]: 'Среда',
  [WeekdayKey.Thursday]: 'Четверг', [WeekdayKey.Friday]: 'Пятница', [WeekdayKey.Saturday]: 'Суббота', [WeekdayKey.Sunday]: 'Воскресенье',
}

function money(value: number): string {
  return `${Math.round(value / 100).toLocaleString('ru-RU')} ₽`
}

function compactMoney(value: number): string {
  const rubles = value / 100
  if (rubles >= 1_000_000) return `${(rubles / 1_000_000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} млн ₽`
  if (rubles >= 1_000) return `${Math.round(rubles / 1_000).toLocaleString('ru-RU')} тыс ₽`
  return `${Math.round(rubles).toLocaleString('ru-RU')} ₽`
}

function monthLabel(month: string, short = false): string {
  return new Intl.DateTimeFormat('ru-RU', {
    month: short ? 'short' : 'long',
    year: short ? undefined : 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${month}-01T00:00:00Z`)).replace('.', '')
}

function unavailableReason(reason: string): string {
  if (reason === 'refund_baseline_missing') return 'Нет закрытых месяцев финансового отчёта для оценки возвратов.'
  if (reason === 'income_estimate_missing') return 'Пока нельзя оценить доход текущего месяца.'
  return 'Оперативный прогноз ещё не рассчитан.'
}

function weekdayValue(point: WeekdayShapePoint, metric: WeekdaySalesMetric): string {
  if (metric === WeekdaySalesMetric.Orders) return point.value.toLocaleString('ru-RU', { maximumFractionDigits: 1 })
  return money(point.value)
}

function WeekdaySalesShape({ products }: { products: ProductAnalyticsPublicDto | null }) {
  const evidence = products?.forecast.weekdaySales
  const anchor = evidence?.coverage?.to || ''
  const [preset, setPreset] = useState<WeekdayShapePreset>(WeekdayShapePreset.ThreeMonths)
  const [metric, setMetric] = useState<WeekdaySalesMetric>(WeekdaySalesMetric.Orders)
  const [customRange, setCustomRange] = useState<WeekdayDateRange>({ from: '', to: '' })
  const [selected, setSelected] = useState<WeekdayKey>(WeekdayKey.Monday)
  const range = useMemo(() => {
    if (preset === WeekdayShapePreset.Custom) return customRange
    return anchor ? weekdayPresetRange(anchor, preset) : customRange
  }, [anchor, customRange, preset])
  useEffect(() => {
    if (!anchor || customRange.to) return
    setCustomRange(weekdayPresetRange(anchor, WeekdayShapePreset.ThreeMonths))
  }, [anchor, customRange.to])
  const shape = useMemo(() => evidence?.coverage
    ? buildWeekdayShape(evidence.daily, range, evidence.coverage, metric)
    : null, [evidence, metric, range])
  useEffect(() => {
    if (shape?.availability === 'available') setSelected(shape.peakWeekday)
  }, [shape])
  const maxValue = shape?.availability === 'available'
    ? Math.max(1, ...shape.points.flatMap(point => [point.value, point.previousValue || 0])) : 1
  const selectedPoint = shape?.availability === 'available' ? shape.points.find(point => point.weekday === selected) : undefined
  const metricTitle = metric === WeekdaySalesMetric.Orders ? 'заказа в среднем' : metric === WeekdaySalesMetric.Turnover ? 'оборота в среднем' : 'средний чек'

  return <div className="surface-card mt-5 overflow-hidden">
    <div className="grid gap-4 border-b border-rule p-4 sm:p-5 xl:grid-cols-[1fr_auto]">
      <div>
        <p className="eyebrow">Ритм спроса</p>
        <h3 className="mt-1 text-[17px] font-semibold sm:text-[19px]">Профиль недели</h3>
        <p className="mt-1 max-w-3xl text-xs leading-relaxed text-ink-muted">Среднее на один календарный день недели. Поэтому пять понедельников не получают преимущество перед четырьмя субботами.</p>
      </div>
      <div className="flex flex-wrap items-start gap-2">
        <div className="inline-flex border border-rule-strong" role="tablist" aria-label="Показатель профиля недели">
          {[[WeekdaySalesMetric.Orders, 'Заказы'], [WeekdaySalesMetric.Turnover, 'Оборот'], [WeekdaySalesMetric.AverageCheck, 'Средний чек']].map(([value, label]) => <ScenarioChoice key={value} selected={metric === value} onSelect={() => setMetric(value as WeekdaySalesMetric)}>{label}</ScenarioChoice>)}
        </div>
      </div>
    </div>
    <div className="flex flex-wrap items-end gap-3 border-b border-rule bg-bone/45 p-4 sm:p-5">
      <div className="inline-flex border border-rule-strong" role="tablist" aria-label="Период профиля недели">
        {[[WeekdayShapePreset.OneMonth, '1 мес.'], [WeekdayShapePreset.ThreeMonths, '3 мес.'], [WeekdayShapePreset.TwelveMonths, '12 мес.']].map(([value, label]) => <ScenarioChoice key={value} selected={preset === value} onSelect={() => setPreset(value as WeekdayShapePreset)}>{label}</ScenarioChoice>)}
      </div>
      <label className="text-[10px] uppercase tracking-[0.1em] text-ink-faint">С даты
        <input aria-label="Начало периода профиля недели" type="date" value={preset === WeekdayShapePreset.Custom ? customRange.from : range.from} max={anchor} onChange={(event) => { setPreset(WeekdayShapePreset.Custom); setCustomRange(current => ({ ...current, from: event.target.value })) }} className="mt-1 block min-h-9 border border-rule-strong bg-paper px-2 text-xs normal-case tracking-normal text-ink" />
      </label>
      <label className="text-[10px] uppercase tracking-[0.1em] text-ink-faint">По дату
        <input aria-label="Конец периода профиля недели" type="date" value={preset === WeekdayShapePreset.Custom ? customRange.to : range.to} max={anchor} onChange={(event) => { setPreset(WeekdayShapePreset.Custom); setCustomRange(current => ({ ...current, to: event.target.value })) }} className="mt-1 block min-h-9 border border-rule-strong bg-paper px-2 text-xs normal-case tracking-normal text-ink" />
      </label>
    </div>
    {!shape || shape.availability === 'unavailable' ? <div className="p-8 text-center">
      <p className="font-semibold">Профиль пока недоступен</p>
      <p className="mt-1 text-xs text-ink-muted">{shape?.reason === 'range_outside_coverage' ? 'Выбранный период находится вне накопленной истории.' : 'В выбранном периоде пока недостаточно продаж.'}</p>
    </div> : <div className="grid xl:grid-cols-[minmax(0,1fr)_280px]">
      <div className="min-w-0 p-4 sm:p-5">
        <div className="grid h-[250px] grid-cols-7 items-end gap-2 border-b border-rule px-1 pt-5" role="img" aria-label="Продажи по дням недели">
          {shape.points.map(point => {
            const height = Math.max(point.value > 0 ? 5 : 0, point.value / maxValue * 190)
            const previousHeight = (point.previousValue || 0) / maxValue * 190
            return <button key={point.weekday} type="button" aria-label={`${WEEKDAY_NAMES[point.weekday]}: ${weekdayValue(point, metric)} ${metricTitle}`} aria-pressed={selected === point.weekday} onClick={() => setSelected(point.weekday)} className="group flex h-full min-w-0 flex-col items-center justify-end focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-deep">
              <span className="mb-1 hidden text-[9px] font-semibold tabular-nums text-ink-muted sm:block">{weekdayValue(point, metric)}</span>
              <span className="relative flex w-full max-w-12 items-end justify-center" style={{ height: 195 }}>
                {previousHeight > 0 && <i className="absolute bottom-0 w-full border border-dashed border-ink-faint/60" style={{ height: previousHeight }} />}
                <i className={`relative w-[72%] transition-colors ${selected === point.weekday ? 'bg-gold-deep' : 'bg-gold/65 group-hover:bg-gold-deep'}`} style={{ height }} />
              </span>
              <span className={`mt-2 text-[11px] ${selected === point.weekday ? 'font-semibold text-ink' : 'text-ink-muted'}`}>{WEEKDAY_LABELS[point.weekday]}</span>
            </button>
          })}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-ink-muted">
          <span><i className="mr-1.5 inline-block h-2.5 w-4 bg-gold/65 align-middle" />Выбранный период</span>
          <span><i className="mr-1.5 inline-block h-2.5 w-4 border border-dashed border-ink-faint align-middle" />Предыдущий равный период</span>
          {shape.quality !== WeekdayEvidenceQuality.Sufficient && <strong className="text-gold-deep">Малая выборка</strong>}
        </div>
      </div>
      <aside className="border-t border-rule bg-bone p-4 xl:border-l xl:border-t-0 sm:p-5" aria-label="Детали выбранного дня недели">
        {selectedPoint && <>
          <p className="eyebrow">{WEEKDAY_NAMES[selectedPoint.weekday]}</p>
          <p className="mt-2 text-2xl font-semibold tabular-nums">{weekdayValue(selectedPoint, metric)}</p>
          <p className="mt-1 text-xs text-ink-muted">{metricTitle} · {selectedPoint.occurrences} вхождений в периоде</p>
          <div className="mt-5 border-t border-rule pt-4 text-xs">
            <p className="text-ink-muted">Заказов: <strong className="text-ink">{selectedPoint.orders}</strong></p>
            <p className="mt-1 text-ink-muted">Оборот: <strong className="text-ink">{money(selectedPoint.turnoverKopecks)}</strong></p>
            <p className="mt-1 text-ink-muted">К прошлому периоду: <strong className="text-ink">{selectedPoint.changeRate === null ? 'нет сравнения' : `${selectedPoint.changeRate >= 0 ? '↑' : '↓'} ${Math.abs(selectedPoint.changeRate * 100).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`}</strong></p>
          </div>
        </>}
      </aside>
    </div>}
    <div className="border-t border-rule px-4 py-3 text-[10px] leading-relaxed text-ink-muted sm:px-5">
      Профиль учитывается в прогнозе текущего месяца только при достаточной выборке и только для ещё не прошедших дней. Корректировка ограничена диапазоном ±15%.
    </div>
  </div>
}

function ScenarioChoice({
  selected,
  onSelect,
  children,
}: {
  selected: boolean
  onSelect: () => void
  children: React.ReactNode
}) {
  return <button
    type="button"
    aria-pressed={selected}
    onClick={onSelect}
    className={`min-h-9 border-r border-rule-strong px-3 text-[11px] last:border-r-0 sm:text-xs ${selected ? 'bg-onyx font-semibold text-ivory' : 'bg-ivory text-ink-muted hover:text-ink'}`}
  >
    {children}
  </button>
}

function ForecastChart({
  points,
  responseEnabled,
}: {
  points: ReinvestmentForecastPoint[]
  responseEnabled: boolean
}) {
  const [selectedMonth, setSelectedMonth] = useState(points[0]?.month || '')
  const [hoveredMonth, setHoveredMonth] = useState<string | null>(null)
  useEffect(() => setSelectedMonth(points[0]?.month || ''), [points])
  const width = 900
  const height = 300
  const padLeft = 72
  const padRight = 20
  const padTop = 24
  const padBottom = 48
  const labels = points.map((point) => point.month)
  const step = labels.length > 1 ? (width - padLeft - padRight) / (labels.length - 1) : 0
  const valueOf = (point: ReinvestmentForecastPoint) => point.scenarioTurnoverKopecks
  const max = Math.max(
    ...points.map((point) => point.highTurnoverKopecks),
    1,
  )
  const chartHeight = height - padTop - padBottom
  const y = (value: number) => padTop + chartHeight * (1 - value / max)
  const forecastCoords = points.map((point, index) => ({ point, x: padLeft + index * step, y: y(valueOf(point)) }))
  const baselineCoords = points.map((point, index) => ({ point, x: padLeft + index * step, y: y(point.baselineTurnoverKopecks) }))
  const flatCoords = points.map((point, index) => ({ point, x: padLeft + index * step, y: y(point.flatTurnoverKopecks) }))
  const line = (coords: { x: number; y: number }[]) => coords.map((point) => `${point.x},${point.y}`).join(' ')
  const corridor = forecastCoords.length > 1
    ? [
        ...points.map((point, index) => `${padLeft + index * step},${y(point.highTurnoverKopecks)}`),
        ...[...points].reverse().map((point, reverseIndex) => {
          const index = points.length - 1 - reverseIndex
          return `${padLeft + index * step},${y(point.lowTurnoverKopecks)}`
        }),
      ].join(' ')
    : ''
  const selectedForecast = points.find((point) => point.month === selectedMonth)
  const hoveredForecastCoord = forecastCoords.find(({ point }) => point.month === hoveredMonth)
  const hoveredCoord = hoveredForecastCoord
  const tooltipWidth = 224
  const tooltipHeight = 82
  const tooltipX = hoveredCoord ? Math.min(width - tooltipWidth - 4, Math.max(4, hoveredCoord.x - tooltipWidth / 2)) : 0
  const tooltipY = hoveredCoord ? Math.max(4, hoveredCoord.y - tooltipHeight - 14) : 0

  return <div className="min-w-0">
    <div className="mb-2 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]" aria-label="Месяц прогноза">
      {points.map((point) => <button
        key={point.month}
        type="button"
        aria-pressed={selectedForecast?.month === point.month}
        onClick={() => setSelectedMonth(point.month)}
        className={`shrink-0 rounded-full border px-3 py-1.5 text-[11px] ${selectedForecast?.month === point.month ? 'border-gold-deep bg-bone font-semibold text-ink' : 'border-rule text-ink-muted'}`}
      >
        {monthLabel(point.month, true)}
      </button>)}
    </div>
    <svg viewBox={`0 0 ${width} ${height}`} className="h-[265px] w-full sm:h-[300px]" role="img" aria-label="Сценарный прогноз по месяцам">
      {[0, 0.5, 1].map((ratio) => {
        const lineY = padTop + chartHeight * ratio
        return <g key={ratio}>
          <line x1={padLeft} x2={width - padRight} y1={lineY} y2={lineY} stroke="var(--color-rule)" strokeWidth="1" />
          <text x={padLeft - 10} y={lineY + 3} textAnchor="end" fontSize="10" fill="var(--color-ink-faint)">{compactMoney(max * (1 - ratio))}</text>
        </g>
      })}
      {corridor && <polygon points={corridor} fill="var(--color-gold)" opacity="0.1" />}
      {flatCoords.length > 1 && <polyline points={line(flatCoords)} fill="none" stroke="var(--color-ink-faint)" strokeWidth="1.5" strokeDasharray="3 5" />}
      {baselineCoords.length > 1 && <polyline points={line(baselineCoords)} fill="none" stroke="var(--color-gold-deep)" strokeWidth="2" strokeDasharray="7 5" />}
      {forecastCoords.length > 1 && <polyline points={line(forecastCoords)} fill="none" stroke={responseEnabled ? 'var(--color-gold-deep)' : 'var(--color-ink)'} strokeWidth="3" strokeLinejoin="round" />}
      {forecastCoords.map(({ point, x, y: pointY }) => <g
        key={point.month}
        data-testid={`forecast-point-${point.month}`}
        onMouseEnter={() => setHoveredMonth(point.month)}
        onMouseLeave={() => setHoveredMonth(null)}
        onClick={() => setSelectedMonth(point.month)}
        className="cursor-pointer"
      >
        <circle cx={x} cy={pointY} r={selectedForecast?.month === point.month ? 6 : 4} fill="var(--color-paper)" stroke="var(--color-gold-deep)" strokeWidth="2" />
        <circle cx={x} cy={pointY} r="14" fill="transparent" />
        <title>{monthLabel(point.month)}: {money(valueOf(point))}</title>
      </g>)}
      {labels.map((month, index) => {
        if (labels.length > 12 && index % 2 === 1 && index !== labels.length - 1) return null
        return <text key={`${month}-${index}`} x={padLeft + index * step} y={height - 14} textAnchor="middle" fontSize="10" fill="var(--color-ink-muted)">{monthLabel(month, true)}</text>
      })}
      {hoveredCoord && <g pointerEvents="none">
        <rect x={tooltipX} y={tooltipY} width={tooltipWidth} height={tooltipHeight} rx="5" fill="var(--color-onyx)" />
        <text x={tooltipX + 12} y={tooltipY + 18} fontSize="10" fontWeight="600" fill="var(--color-gold-soft)" letterSpacing="0.8">{monthLabel(hoveredCoord.point.month).toUpperCase()}</text>
        {hoveredForecastCoord && <>
          <text x={tooltipX + 12} y={tooltipY + 37} fontSize="11" fill="var(--color-ivory)">Сценарий: {money(valueOf(hoveredForecastCoord.point))}</text>
          <text x={tooltipX + 12} y={tooltipY + 53} fontSize="10" fill="rgba(250,247,241,.68)">База: {money(hoveredForecastCoord.point.baselineTurnoverKopecks)}</text>
          <text x={tooltipX + 12} y={tooltipY + 68} fontSize="10" fill="rgba(250,247,241,.68)">В товар: {money(hoveredForecastCoord.point.reinvestedThisMonthKopecks)} · +{(hoveredForecastCoord.point.upliftRate * 100).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%</text>
        </>}
      </g>}
    </svg>
    {selectedForecast && <div className="grid grid-cols-2 gap-2 border-t border-rule pt-3 text-xs sm:grid-cols-4">
      <div><span className="block text-ink-faint">Месяц</span><strong>{monthLabel(selectedForecast.month)}</strong></div>
      <div><span className="block text-ink-faint">База по сезону</span><strong>{money(selectedForecast.baselineTurnoverKopecks)}</strong></div>
      <div><span className="block text-ink-faint">Сценарий продаж</span><strong>{money(valueOf(selectedForecast))}</strong></div>
      <div><span className="block text-ink-faint">Возвращено в товар</span><strong>{money(selectedForecast.reinvestedThisMonthKopecks)}</strong></div>
    </div>}
  </div>
}

export function ForecastsSection({
  products,
  returns,
  embedded = false,
}: {
  products: ProductAnalyticsPublicDto | null
  returns: AnalyticsReturns
  embedded?: boolean
}) {
  const [share, setShare] = useState(0.75)
  const [responseEnabled, setResponseEnabled] = useState(false)

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(PREFERENCES_KEY) || 'null') as {
        share?: number
        responseEnabled?: boolean
      } | null
      if (SHARE_OPTIONS.includes(stored?.share as typeof SHARE_OPTIONS[number])) setShare(stored!.share!)
      setResponseEnabled(stored?.responseEnabled === true)
    } catch {}
  }, [])

  useEffect(() => {
    try { localStorage.setItem(PREFERENCES_KEY, JSON.stringify({ share, responseEnabled })) } catch {}
  }, [share, responseEnabled])

  const forecast = useMemo(() => buildReinvestmentForecast({
    operational: products?.forecast.operational || null,
    monthly: products?.monthly || [],
    materials: products?.materials || [],
    refundRate: returns.baseline?.rate ?? null,
    reinvestmentShare: share,
    horizon: ForecastHorizon.TwelveMonths,
    profile: ForecastProfile.Seasonal,
    includeReinvestmentResponse: responseEnabled,
  }), [products, returns.baseline, share, responseEnabled])

  const lastPoint = forecast.availability === 'available' ? forecast.points.at(-1) : undefined
  const uplift = lastPoint ? lastPoint.scenarioTurnoverKopecks - lastPoint.baselineTurnoverKopecks : 0
  const agreement = forecast.availability === 'available' ? forecast.directionalAgreement : null

  return <section id="forecasts" className={`analytics-anchor ${embedded ? '' : 'mb-9 sm:mb-12'}`} aria-labelledby={embedded ? undefined : 'forecasts-title'}>
    <div className="mb-3">
      {!embedded && <>
        <p className="eyebrow">Планирование</p>
        <h2 id="forecasts-title" className="mt-1 text-[21px] font-semibold tracking-tight sm:text-[24px]">Прогнозы</h2>
      </>}
      <p className="mt-1 max-w-3xl text-xs leading-relaxed text-ink-muted">Дорожная карта продаж по сезонному шейпу и сценарий возврата дохода в товар.</p>
    </div>
    <div className="surface-card overflow-hidden">
      <div className="grid gap-4 border-b border-rule p-4 sm:p-5 xl:grid-cols-[1fr_auto]">
        <div>
          <h3 className="text-[17px] font-semibold sm:text-[19px]">Продажи и реинвестирование</h3>
          <p className="mt-1 max-w-3xl text-xs leading-relaxed text-ink-muted">
            Прогноз на 12 месяцев использует внешний сезонный ориентир; он сверяется с нашей историей, но не является обещанием результата.
          </p>
        </div>
        <label className="inline-flex min-h-10 cursor-pointer items-center gap-2 self-start border border-rule-strong bg-ivory px-3 text-xs font-medium">
          <input type="checkbox" checked={responseEnabled} onChange={(event) => setResponseEnabled(event.target.checked)} className="h-4 w-4 accent-[#94733f]" />
          Сценарий роста от реинвестирования
        </label>
      </div>
      <div className="border-b border-rule bg-bone/45 p-4 sm:p-5">
        <div>
          <p className="mb-1.5 text-[10px] uppercase tracking-[0.12em] text-ink-faint">Доля дохода</p>
          <div className="inline-flex border border-rule-strong" role="group" aria-label="Доля реинвестирования дохода">
            {SHARE_OPTIONS.map((value) => <ScenarioChoice key={value} selected={share === value} onSelect={() => setShare(value)}>{value * 100}%</ScenarioChoice>)}
          </div>
        </div>
      </div>
      {forecast.availability === 'unavailable' ? (
        <div className="p-8 text-center">
          <p className="font-semibold">Прогноз пока недоступен</p>
          <p className="mt-1 text-xs text-ink-muted">{unavailableReason(forecast.reason)}</p>
        </div>
      ) : (
        <>
          <div className="grid xl:grid-cols-[minmax(0,1fr)_280px]">
            <div className="min-w-0 p-3 sm:p-5">
              <ForecastChart points={forecast.points} responseEnabled={responseEnabled} />
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-[10px] text-ink-muted">
                <span><i className="mr-1.5 inline-block w-5 border-t-2 border-dashed border-gold-deep align-middle" />Сезонная база</span>
                <span><i className="mr-1.5 inline-block w-5 border-t border-dashed border-ink-faint align-middle" />Ровный ориентир</span>
                <span><i className="mr-1.5 inline-block h-2 w-5 bg-gold/15 align-middle" />Рабочий коридор</span>
              </div>
            </div>
            <aside className="border-t border-rule bg-bone p-4 xl:border-l xl:border-t-0 sm:p-5">
              <div className="space-y-5">
                <div>
                  <p className="eyebrow">К концу сценария</p>
                  <p className="mt-2 text-2xl font-semibold tabular-nums">{lastPoint ? money(lastPoint.scenarioTurnoverKopecks) : '—'}</p>
                  <p className="mt-1 text-xs text-ink-muted">{responseEnabled ? `+ ${money(uplift)} к сезонной базе, не более ${(forecast.maximumUpliftRate * 100).toLocaleString('ru-RU')}% в месяц` : 'Сезонная база без эффекта реинвестирования'}</p>
                </div>
                <div>
                  <p className="eyebrow">Вернётся в товар</p>
                  <p className="mt-2 text-xl font-semibold tabular-nums">{lastPoint ? money(lastPoint.cumulativeReinvestmentKopecks) : '—'}</p>
                  <p className="mt-1 text-xs text-ink-muted">{Math.round(share * 100)}% оценочного дохода после исторической доли возвратов.</p>
                </div>
                <div>
                  <p className="eyebrow">Сверка шейпа</p>
                  <p className="mt-2 text-sm font-semibold">{agreement ? `${agreement.matched} из ${agreement.compared} направлений совпали` : 'Недостаточно переходов'}</p>
                  <p className="mt-1 text-xs leading-relaxed text-ink-muted">Есть только {forecast.ownHistory.length} мес. собственной истории — сезонность пока не подтверждена.</p>
                </div>
              </div>
            </aside>
          </div>
          <div className="grid gap-3 border-t border-rule px-4 py-3 text-[10px] leading-relaxed text-ink-muted sm:grid-cols-2 sm:px-5">
            <p><strong className="text-ink">Факты:</strong> месяцы Almaz, оперативный прогноз текущего месяца и возвраты из финансового отчёта.</p>
            <p>
              <strong className="text-ink">Сценарий:</strong> национальный шейп розничных продаж украшений 2025, месячный лаг и ограниченный отклик капитала. Это рабочая гипотеза, не статистическое доказательство.{' '}
              <a href="https://probpalata.gov.ru/category/statistika/" target="_blank" rel="noreferrer" className="underline decoration-rule-strong underline-offset-2 hover:text-ink">Источник сезонного шейпа</a>.
            </p>
          </div>
        </>
      )}
    </div>
    <WeekdaySalesShape products={products} />
  </section>
}
